/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import Set from 'fierry/type/set';
import Map from 'fierry/type/map';
import Hex from 'solis/type/hex';


export default class HexGroup
{
  constructor (collection)
  {
    this.collection = new Set();

    this.append(collection);
  }


  append (collection)
  {
    for (const element of collection)
    {
      this.collection.insert(element);
    }
  }


  erase (collection)
  {
    for (const element of collection)
    {
      this.collection.remove(element);
    }
  }


  rotateLeft (count)
  {
    return new HexGroup(this.collection.map(element =>
    {
      return element.rotateLeft(count);
    }));
  }


  rotateRight (count)
  {
    return new HexGroup(this.collection.map(element =>
    {
      return element.rotateRight(count);
    }));
  }


  transform (offset, rotate = 0)
  {
    return new HexGroup(this.collection.map(element =>
    {
      return element.transform(offset, rotate);
    }));
  }


  transformWithObject (object)
  {
    return this.transform(object.offset, object.rotation);
  }


  normalize ()
  {
    return this.transform(this.at(0).negate());
  }


  at (index)
  {
    return this.collection.at(index);
  }


  getCenter ()
  {
    let center = new Hex(0, 0);

    for (const element of this.collection)
    {
      center = center.add(element);
    }

    return center.divide(this.size());
  }


  getFrontier ()
  {
    let frontier = new Set();

    for (const element of this.collection)
    {
      for (const neighbor of element.getNeighbors())
      {
        if (!this.collection.contains(neighbor))
        {
          frontier.insert(element);

          break;
        }
      }
    }

    return new HexGroup(frontier);
  }


  getNeighbors ()
  {
    return this.getNeighborsWithExclude(this, null);
  }


  getNeighborsWithFilter (filterCallback)
  {
    return this.getNeighborsWithExclude(this, filterCallback);
  }


  getNeighborsWithExclude (exclude, filterCallback = null)
  {
    let neighbors = new Map();

    for (const element of this.collection)
    {
      for (const neighbor of element.getNeighbors())
      {
        if (!exclude.contains(neighbor))
        {
          neighbors.set(neighbor, (neighbors.get(neighbor) ?? 0) + 1);
        }
      }
    }

    let filteredNeighbors = new Set();

    for (const { key, value } of neighbors)
    {
      if (filterCallback == null || filterCallback(key, value, neighbors))
      {
        filteredNeighbors.insert(key);
      }
    }

    return new HexGroup(filteredNeighbors);
  }


  toString ()
  {
    let parts = [];

    for (const hex of this.collection)
    {
      parts.push(hex.toString());
    }

    return parts.join(' ');
  }


  contains (element)
  {
    return this.collection.contains(element);
  }


  size ()
  {
    return this.collection.size();
  }


  empty ()
  {
    return this.collection.empty();
  }


  toArray ()
  {
    return [ ...this ];
  }


  equals (rhs)
  {
    if (this.size() !== rhs.size())
    {
      return false;
    }

    for (let i = 0; i < this.size(); ++i)
    {
      if (!this.at(i).equals(rhs.at(i)))
      {
        return false;
      }
    }

    return true;
  }


  *[Symbol.iterator]()
  {
    for (const element of this.collection)
    {
      yield element;
    }
  }

};
